import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, Sparkles, CheckCircle2, XCircle } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { getApiUrl } from "@/utils/api";
import { IntegrationApiLogToggle } from "./IntegrationApiLogToggle";

const API = getApiUrl();

/**
 * Admin -> Integrations -> Claude AI
 * Powers the CMS "AI" button (Text/HTML module), supplier job description
 * generator, and per-product job description generator. Uses the shared
 * Emergent LLM Key — nothing for the admin to type in, just status + a
 * live test call, plus the shared error-logging switch.
 */
export default function ClaudeAiIntegrationSection() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/integrations/claude-ai-status`, { withCredentials: true });
      setStatus(data);
    } catch (e) {
      console.error("Failed to load Claude AI status:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleTest = async () => {
    setTesting(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/integrations/claude-ai-test-call`, {}, { withCredentials: true });
      setResult({ ok: true, response: data.response, duration_ms: data.duration_ms });
      toast.success(`Claude AI responded in ${data.duration_ms}ms`);
    } catch (e) {
      setResult({ ok: false, error: e?.response?.data?.detail || "Test call failed" });
      toast.error(e?.response?.data?.detail || "Test call failed");
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="claude-ai-loading">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="claude-ai-integration-section">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-4 w-4" />
            Claude AI
          </CardTitle>
          <CardDescription>
            Powers the "AI" button in the Text/HTML module editor, and the supplier / product
            "Become a Preferred Supplier" description generators. Uses the shared Emergent LLM
            Key — no API key to configure here.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2 text-sm" data-testid="claude-ai-status">
            {status?.configured ? (
              <>
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span className="font-medium">Connected</span>
                <span className="text-muted-foreground">— {status.model} ({status.provider})</span>
              </>
            ) : (
              <>
                <XCircle className="h-4 w-4 text-destructive" />
                <span className="font-medium text-destructive">Emergent LLM Key not configured</span>
              </>
            )}
          </div>

          <div className="text-xs text-muted-foreground space-y-1">
            <p>Request timeout: {status?.timeout_seconds}s (long/complex prompts are cancelled with a clear error instead of hanging)</p>
            <p>Used by: {status?.used_by?.join(" · ")}</p>
          </div>

          <div className="text-xs rounded-md p-3 border border-border bg-muted/30" data-testid="claude-ai-credits-help">
            <p className="font-medium text-foreground">Need more credits?</p>
            <p className="text-muted-foreground mt-0.5">
              Each request spends Universal Key balance. Go to <span className="font-medium text-foreground">Profile → Manage Plan → Universal Key → Add Balance</span> to top up, or turn on auto top-up so you never run out mid-task.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Button
              onClick={handleTest}
              disabled={testing || !status?.configured}
              size="sm"
              data-testid="claude-ai-test-call-btn"
            >
              {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Send Test Prompt
            </Button>
          </div>

          {result && (
            <div
              className={`text-sm rounded-md p-3 border ${result.ok ? "border-green-200 bg-green-50 text-green-800" : "border-destructive/30 bg-destructive/5 text-destructive"}`}
              data-testid="claude-ai-test-result"
            >
              {result.ok ? (
                <>
                  <p className="font-medium mb-1">Success in {result.duration_ms}ms</p>
                  <p className="font-mono text-xs break-all">{result.response}</p>
                </>
              ) : (
                <p>{result.error}</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <IntegrationApiLogToggle provider="claude_ai" />
    </div>
  );
}
